import { useState, type FormEvent } from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  QueryStateBoundary,
  toastError,
  toastSuccess,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import {
  useTwoFactorDisable,
  useTwoFactorEnroll,
  useTwoFactorStatus,
  useTwoFactorVerify,
} from "@/api/hooks/useTwoFactor";

type Mode = "idle" | "enrolling" | "disabling";

/**
 * Opt-in TOTP 2FA settings. Reachable at /security (small nav link in
 * AppShell) — never in the login path itself, so an account that hasn't
 * opted in never sees this page unless it navigates here directly.
 */
export function SecurityPage() {
  const status = useTwoFactorStatus();
  const enroll = useTwoFactorEnroll();
  const verify = useTwoFactorVerify();
  const disable = useTwoFactorDisable();

  const [mode, setMode] = useState<Mode>("idle");
  const [enrollment, setEnrollment] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  async function handleStartEnroll() {
    try {
      const result = await enroll.mutateAsync();
      setEnrollment(result);
      setCode("");
      setMode("enrolling");
    } catch (err) {
      toastError("Could not start enrollment", getErrorMessage(err));
    }
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    try {
      await verify.mutateAsync({ code });
      toastSuccess("Two-factor authentication enabled");
      setMode("idle");
      setEnrollment(null);
      setCode("");
    } catch (err) {
      toastError("Invalid code", getErrorMessage(err));
    }
  }

  async function handleDisable(e: FormEvent) {
    e.preventDefault();
    try {
      await disable.mutateAsync({ code });
      toastSuccess("Two-factor authentication disabled");
      setMode("idle");
      setCode("");
    } catch (err) {
      toastError("Invalid code", getErrorMessage(err));
    }
  }

  async function handleCopy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied — the text is still selectable/visible.
    }
  }

  function cancel() {
    setMode("idle");
    setEnrollment(null);
    setCode("");
  }

  return (
    <div className="p-6 sm:p-8 max-w-2xl">
      <h1 className="font-display text-2xl font-bold text-text-primary mb-6">Security</h1>

      <QueryStateBoundary
        isLoading={status.isLoading}
        error={status.error}
        onRetry={() => status.refetch()}
      >
        <Card>
          <CardHeader>
            <CardTitle>Two-factor authentication</CardTitle>
            <CardDescription>
              Optional. Adds a 6-digit code from an authenticator app (Google Authenticator, Authy,
              1Password, etc.) on top of your password at sign-in.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mode === "idle" && (
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    Status:{" "}
                    <span className={status.data?.enabled ? "text-success" : "text-text-muted"}>
                      {status.data?.enabled ? "Enabled" : "Not enabled"}
                    </span>
                  </p>
                </div>
                {status.data?.enabled ? (
                  <Button variant="danger" onClick={() => setMode("disabling")}>
                    Disable
                  </Button>
                ) : (
                  <Button onClick={handleStartEnroll} disabled={enroll.isPending}>
                    {enroll.isPending ? "Starting..." : "Enable 2FA"}
                  </Button>
                )}
              </div>
            )}

            {mode === "enrolling" && enrollment && (
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-text-secondary">
                    Scan this into your authenticator app, or enter it manually if it doesn't support
                    scanning a URI:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 break-all rounded-md border border-border bg-bg-body px-3 py-2 text-xs text-text-primary">
                      {enrollment.otpauth_uri}
                    </code>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleCopy(enrollment.otpauth_uri)}
                    >
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                  <p className="text-sm text-text-secondary">
                    Manual entry key: <code className="font-mono text-text-primary">{enrollment.secret}</code>
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="verify-code">Enter the code from your app to confirm</Label>
                  <Input
                    id="verify-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    autoFocus
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" disabled={verify.isPending || code.length < 6}>
                    {verify.isPending ? "Verifying..." : "Confirm & enable"}
                  </Button>
                  <Button type="button" variant="ghost" onClick={cancel}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {mode === "disabling" && (
              <form onSubmit={handleDisable} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="disable-code">
                    Enter your current authenticator code to disable 2FA
                  </Label>
                  <Input
                    id="disable-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    autoFocus
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" variant="danger" disabled={disable.isPending || code.length < 6}>
                    {disable.isPending ? "Disabling..." : "Disable 2FA"}
                  </Button>
                  <Button type="button" variant="ghost" onClick={cancel}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </QueryStateBoundary>
    </div>
  );
}
